import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useJitsu } from '@jitsu/jitsu-react';
import { useQueryClient } from '@tanstack/react-query';

import {
  Button,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@tg-web/components';

import { useOverlapEventsV2 } from '../../../shared/api';
import { EventsAnalytics } from '../../../shared/model/analytics';
import { EventInfoCard } from './EventInfoCard';

export type OverlapEventsProps = {
  duration: string;
  enabled?: boolean;
  onCancel?: () => void;
  onContinue?: () => void;
  start_at: string;
};

export function OverlapEvents({
  duration,
  enabled,
  onCancel,
  onContinue,
  start_at,
}: OverlapEventsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { analytics } = useJitsu();

  const overlapEvents = useOverlapEventsV2(
    {
      queryParams: {
        duration,
        start_at,
      },
    },
    { enabled },
  );

  useEffect(() => {
    if (!enabled) {
      // remove previous overlap events data, to prevent errors on repeated modal calls
      queryClient.removeQueries({
        queryKey: ['events', 'overlap'],
      });
    }
  }, [enabled, queryClient]);

  useEffect(() => {
    if (
      enabled &&
      overlapEvents.isSuccess &&
      !overlapEvents.isFetching &&
      overlapEvents.data.events.length === 0
    ) {
      onContinue?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    overlapEvents.isSuccess,
    overlapEvents.isFetching,
    overlapEvents.data?.events.length,
    enabled,
  ]);

  const isMulti =
    overlapEvents.isSuccess && overlapEvents.data.events.length > 1;

  const isOpen =
    enabled && overlapEvents.isSuccess && overlapEvents.data.events.length > 0;

  // onContinue also fires silently when nothing overlaps, so the sheet
  // becoming visible is the only signal that the user was actually asked.
  useEffect(() => {
    if (isOpen) {
      analytics.track(EventsAnalytics.OVERLAP_SHOWN, {
        overlapCount: overlapEvents.data?.events.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleChoice = (choice: 'cancel' | 'continue' | 'dismiss') => () => {
    analytics.track(EventsAnalytics.OVERLAP_CHOICE, { choice });
    if (choice === 'continue') {
      onContinue?.();
    } else {
      onCancel?.();
    }
  };

  return (
    <Sheet onOpenChange={handleChoice('dismiss')} open={isOpen}>
      <SheetContent
        className="flex w-full flex-col gap-0 overflow-y-scroll"
        side="bottom"
      >
        <SheetHeader>
          <SheetTitle>{t('all:event.overlap_dialog.title')}</SheetTitle>
        </SheetHeader>
        <p className="typo-text text-tg-subtitle-text mt-4">
          {t(
            isMulti
              ? 'all:event.overlap_dialog.multi.subtitle'
              : 'all:event.overlap_dialog.subtitle',
          )}
        </p>
        {overlapEvents.isSuccess && (
          <>
            <EventInfoCard
              className="mt-6"
              event={overlapEvents.data.events[0]}
              isMulti={isMulti}
            />
            {isMulti && (
              <p className="typo-text text-tg-subtitle-text mt-6 text-center">
                {t('all:event.overlap_dialog.multi.count', {
                  count: overlapEvents.data.events.length,
                })}
              </p>
            )}
          </>
        )}
        <Button
          className="mt-8"
          onClick={handleChoice('continue')}
          type="button"
          variant="primary"
        >
          {t('all:common.buttons.continue')}
        </Button>
        <Button
          className="mb-6 mt-3"
          onClick={handleChoice('cancel')}
          type="button"
          variant="secondary"
        >
          {t('all:common.buttons.cancel')}
        </Button>
      </SheetContent>
    </Sheet>
  );
}
